import doc                      from '@widesk-core/decorators/doc';
import model                    from '@widesk-core/decorators/model';
import MapServiceModel          from '@/models/MapServiceModel';
import TimestampAble            from '@widesk-core/models/traits/TimestampAble';
import Blamable                 from '@/models/traits/Blamable';
import type { ShapeTextStyle }  from '@tokamap/core';
import type { ShapeImageStyle } from '@tokamap/core';

export type ThemeScope = 'normal' | 'hover';

/**
 * Mêmes noms que `ShapeStyle` de `@tokamap/core`, pour qu'appliquer un thème à une forme reste une
 * correspondance directe.
 *
 * Champ par champ : ce que le thème renseigne s'impose aux formes liées, un champ vide laisse celui
 * de chaque forme (voir `applyShapeTheme`).
 */
export type ThemeStyle = {
	borderColor?: string | null;
	bgColor?: string | null;
	borderWidth?: number | null;
	borderWidthUnit?: 'px' | 'm' | null;
	borderDashed?: boolean | null;
};

/** `null` retire un réglage : l'API patche en `merge-patch+json`, où une clé omise garderait l'ancienne valeur. */
type Clearable<T> = { [K in keyof T]?: T[K] | null };

/**
 * Présentation du texte portée par un thème, jamais son contenu : une forme sans texte n'affiche rien
 * et le thème ne peut pas l'inventer (voir `ShapeTheme.text` dans `@tokamap/core`). Comme pour
 * `ThemeStyle`, une couleur vide ne retombe pas sur celle de la forme mais sur le défaut de
 * l'adapter ; tout autre réglage vide laisse celui de la forme.
 */
export type ThemeTextStyle = Clearable<ShapeTextStyle>;

/** Présentation de l'image portée par un thème, jamais sa source ; un réglage vide laisse celui de la forme. */
export type ThemeImageStyle = Clearable<ShapeImageStyle>;

/**
 * `bgColor` et `borderColor` à la racine sont exigés par le schéma `ThemeConfiguration` de l'API, qui
 * rejette la configuration sans eux ; aucun écran ne les lit, tous passent par `appearance`. Le
 * schéma accepte toute autre clé sans en valider le contenu, d'où la normalisation de `themeStore`.
 */
export type ThemeConfiguration = {
	bgColor: string;
	borderColor: string;
	appearance: Record<ThemeScope, ThemeStyle>;
	text: Record<ThemeScope, ThemeTextStyle>;
	image?: ThemeImageStyle | null;
	/** Absente ou `null` : chaque forme liée garde sa propre hauteur. `null` plutôt qu'une clé omise
	 * pour la retirer, l'API patchant en `merge-patch+json`. */
	height?: number | null;
	/** Remplace celle des formes liées : absente ou `null`, elles restent posées au sol. */
	elevation?: number | null;
	/** Au survol ; absente ou `null`, celle de `height`. */
	hoverHeight?: number | null;
	/** Mêmes principes que `hoverHeight`, pour `elevation`. */
	hoverElevation?: number | null;
	/** Mêmes principes que `height`, pour `Shape.minZoomOffset`. */
	minZoomOffset?: number | null;
	/** De 0 à 1, remplace celle des formes liées : absente ou `null`, l'opacité par défaut des volumes. */
	volumeOpacity?: number | null;
	/** Un marqueur impose l'image et le texte des formes liées en marqueur (voir `ShapeBadge`), `false`
	 * les impose dedans, `null` laisse chaque forme décider. Chaque clé du marqueur est écrite, `null`
	 * comprise : en `merge-patch+json`, une clé omise garderait l'ancienne valeur. */
	badge?: ThemeBadge | false | null;
};

export type ThemeBadge = {
	style: 'disc' | 'pin' | 'tag' | null;
	anchor: 'center' | 'top' | null;
	showText: boolean | 'hover' | null;
	textPosition: 'above' | 'below' | null;
	color: string | null;
	size: 'small' | 'medium' | 'large' | null;
	shape: 'round' | 'square' | null;
	minZoomOffset: number | null;
};

@model.urnResource('theme')
@doc.path('/themes/{?id}')
export default class ThemeModel extends Blamable(TimestampAble(MapServiceModel)) {
	private declare _tsFilters: {
		'createdAt': string;
		'placeTypes': id;
		'project': id;
		'updatedAt': string | Date | Dayjs;
	};

	private declare _tsSorts: {
		'createdAt': string;
		'id': string;
		'label': string;
		'updatedAt': string;
	};

	@doc.string declare label: string;
	@doc.json declare configuration: ThemeConfiguration;
}
